import * as THREE from "three";
import { Chunk, CHUNK_X, CHUNK_Z, WORLD_HEIGHT } from "../world/chunk";
import { TILE_UV } from "../world/atlas";
import { BLOCKS, COBWEB, isLiquid, shouldRenderFace, TILE, TORCH } from "../world/blocks";
import type { ChunkMesher, World } from "../world/world";

/**
 * Face-culling chunk mesher.
 *
 * For every visible block face it emits one quad, with per-vertex ambient
 * occlusion derived from the three voxels touching each corner. Face brightness
 * and AO are baked into vertex colours, which lets a single `MeshBasicMaterial`
 * cover the whole world — no lights, no shadow maps, and the day/night tint is a
 * single `material.color` change.
 */

type V3 = [number, number, number];

interface FaceDef {
  readonly dir: V3;
  /** Corner of the block the face is anchored to (local 0..1 coordinates). */
  readonly base: V3;
  /** In-plane axis 1; corners advance along it as `a` goes 0 -> 1. */
  readonly u: V3;
  /** In-plane axis 2; corners advance along it as `b` goes 0 -> 1. */
  readonly v: V3;
  /** Index of the axis `u` runs along, and of the axis `v` runs along. */
  readonly axisU: 0 | 1 | 2;
  readonly axisV: 0 | 1 | 2;
  /**
   * Sign of `u` / `v` along their own axis. Needed because a face may run its
   * in-plane vectors against the positive axis, which flips which neighbouring
   * cell sits "beyond" a corner for ambient occlusion.
   */
  readonly signU: 1 | -1;
  readonly signV: 1 | -1;
  /** Fixed directional shading, matching Minecraft's per-face brightness. */
  readonly shade: number;
}

/**
 * Each face satisfies `u x v == dir`, so emitting corners as
 * `base + a*u + b*v` with triangles (0,1,2)(0,2,3) is front-facing by construction.
 * `base` must also sit on the cube side `dir` points at (axis component 1 for a
 * positive direction, 0 for a negative one), or the quad lands inside the block.
 *
 * Every side face uses `u = +Y` so vertical textures (grass fringe, planks, log
 * bark) stay upright no matter which wall they are on.
 */
const FACES: readonly FaceDef[] = [
  // +Y — top
  { dir: [0, 1, 0], base: [0, 1, 0], u: [0, 0, 1], v: [1, 0, 0], axisU: 2, axisV: 0, signU: 1, signV: 1, shade: 1.0 },
  // -Y — bottom
  { dir: [0, -1, 0], base: [0, 0, 0], u: [1, 0, 0], v: [0, 0, 1], axisU: 0, axisV: 2, signU: 1, signV: 1, shade: 0.5 },
  // +X
  { dir: [1, 0, 0], base: [1, 0, 0], u: [0, 1, 0], v: [0, 0, 1], axisU: 1, axisV: 2, signU: 1, signV: 1, shade: 0.8 },
  // -X
  { dir: [-1, 0, 0], base: [0, 0, 1], u: [0, 1, 0], v: [0, 0, -1], axisU: 1, axisV: 2, signU: 1, signV: -1, shade: 0.8 },
  // +Z
  { dir: [0, 0, 1], base: [1, 0, 1], u: [0, 1, 0], v: [-1, 0, 0], axisU: 1, axisV: 0, signU: 1, signV: -1, shade: 0.6 },
  // -Z
  { dir: [0, 0, -1], base: [0, 0, 0], u: [0, 1, 0], v: [1, 0, 0], axisU: 1, axisV: 0, signU: 1, signV: 1, shade: 0.6 },
];

const AO_LEVELS = [0.42, 0.66, 0.84, 1.0] as const;

/** Water surface sits slightly below a full block so shorelines read correctly. */
const WATER_TOP = 0.88;

class MeshBuffer {
  positions: number[] = [];
  normals: number[] = [];
  uvs: number[] = [];
  colors: number[] = [];
  indices: number[] = [];

  get isEmpty(): boolean {
    return this.indices.length === 0;
  }

  toGeometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.positions, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(this.normals, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(this.uvs, 2));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.colors, 3));
    g.setIndex(this.indices);
    g.computeBoundingSphere();
    return g;
  }
}

export class ChunkMesherImpl implements ChunkMesher {
  constructor(
    private readonly solidMaterial: THREE.Material,
    private readonly waterMaterial: THREE.Material,
    /** Scene node that chunk meshes are attached to. */
    private readonly parent: THREE.Object3D,
  ) {}

  mesh(chunk: Chunk, world: World): void {
    const solid = new MeshBuffer();
    const water = new MeshBuffer();

    const ox = chunk.originX;
    const oz = chunk.originZ;
    const blocks = chunk.blocks;

    for (let y = 0; y < WORLD_HEIGHT; y++) {
      for (let lz = 0; lz < CHUNK_Z; lz++) {
        const wz = oz + lz;
        for (let lx = 0; lx < CHUNK_X; lx++) {
          const wx = ox + lx;
          const id = blocks[lx + CHUNK_X * lz + CHUNK_X * CHUNK_Z * y];
          if (id === 0) continue;

          if (id === TORCH) {
            this.emitTorch(solid, wx, y, wz);
            continue;
          }

          if (id === COBWEB) {
            this.emitWeb(solid, wx, y, wz);
            continue;
          }

          const liquid = isLiquid(id);
          const buf = liquid ? water : solid;
          const def = BLOCKS[id];

          for (let f = 0; f < 6; f++) {
            const face = FACES[f];
            const nx = wx + face.dir[0];
            const ny = y + face.dir[1];
            const nz = wz + face.dir[2];

            const neighbour = world.getBlock(nx, ny, nz);
            if (!shouldRenderFace(id, neighbour)) continue;

            const tile = this.tileForFace(def, face.dir);
            const uv = TILE_UV[tile];

            this.emitFace(buf, {
              ox,
              oz,
              lx,
              y,
              lz,
              face,
              uv,
              neighbour,
              faceShade: face.shade,
              liquid,
              world,
            });
          }
        }
      }
    }

    this.applyToChunk(chunk, solid, water);
  }

  private tileForFace(def: (typeof BLOCKS)[number], dir: V3): number {
    if (dir[1] > 0) return def.top;
    if (dir[1] < 0) return def.bottom;
    return def.side;
  }

  private emitFace(
    buf: MeshBuffer,
    ctx: {
      ox: number;
      oz: number;
      lx: number;
      y: number;
      lz: number;
      face: FaceDef;
      uv: { u0: number; v0: number; u1: number; v1: number };
      neighbour: number;
      faceShade: number;
      liquid: boolean;
      world: World;
    },
  ): void {
    const { ox, oz, lx, y, lz, face, uv, neighbour, faceShade, liquid, world } = ctx;
    const bx = ox + lx;
    const bz = oz + lz;

    const baseIndex = buf.positions.length / 3;

    // Corner (a, b) in the face's u/v basis, in emission order.
    const CORNERS: ReadonlyArray<readonly [number, number]> = [
      [0, 0],
      [0, 1],
      [1, 1],
      [1, 0],
    ];

    const ao: number[] = [1, 1, 1, 1];
    const neighbourOpaque = isOccluder(neighbour);
    const torchLight =
      world.torches.size > 0
        ? world.getTorchLight(bx + face.dir[0], y + face.dir[1], bz + face.dir[2], 9)
        : 0;

    for (let i = 0; i < 4; i++) {
      const [pa, pb] = CORNERS[i];
      let px = bx + face.base[0] + face.u[0] * pa + face.v[0] * pb;
      let py = y + face.base[1] + face.u[1] * pa + face.v[1] * pb;
      let pz = bz + face.base[2] + face.u[2] * pa + face.v[2] * pb;

      // Liquids render short so shorelines read correctly on every face.
      if (liquid && py > y) py = y + WATER_TOP;

      buf.positions.push(px, py, pz);
      buf.normals.push(face.dir[0], face.dir[1], face.dir[2]);

      if (!liquid && neighbourOpaque) {
        ao[i] = 1;
      } else {
        // Sample the three voxels diagonally around this corner on the far side
        // of the face.
        const du = (pa ? 1 : -1) * face.signU;
        const dv = (pb ? 1 : -1) * face.signV;
        const s1 = sampleCorner(world, bx, y, bz, face, du, 0, face.dir);
        const s2 = sampleCorner(world, bx, y, bz, face, 0, dv, face.dir);
        const co = sampleCorner(world, bx, y, bz, face, du, dv, face.dir);
        ao[i] = AO_LEVELS[vertexAO(s1, s2, co)];
      }

      const shade = faceShade * ao[i];
      if (torchLight > 0) {
        const boost = torchLight * 0.85;
        const r = Math.min(1.0, shade + boost);
        const g = Math.min(1.0, shade + boost * 0.88);
        const b = Math.min(1.0, shade + boost * 0.65);
        buf.colors.push(r, g, b);
      } else {
        buf.colors.push(shade, shade, shade);
      }
    }

    // `u` is the texture's vertical axis, so `a` drives v and `b` drives u.
    buf.uvs.push(uv.u0, uv.v0, uv.u1, uv.v0, uv.u1, uv.v1, uv.u0, uv.v1);

    // Flip the split so the diagonal joins the two brighter corners; otherwise
    // AO gradients show a visible diagonal seam.
    //
    // Corners are emitted base, base+v, base+u+v, base+u, so traversing them in
    // index order runs *against* the face normal (v x u == -dir). The index
    // buffers below therefore walk the quad in reverse to keep every face
    // counter-clockwise when seen from outside, as Three.js expects.
    if (ao[0] + ao[2] > ao[1] + ao[3]) {
      // Split along the 1-3 diagonal.
      buf.indices.push(
        baseIndex + 1, baseIndex + 0, baseIndex + 3,
        baseIndex + 1, baseIndex + 3, baseIndex + 2,
      );
    } else {
      // Split along the 0-2 diagonal.
      buf.indices.push(
        baseIndex + 0, baseIndex + 3, baseIndex + 2,
        baseIndex + 0, baseIndex + 2, baseIndex + 1,
      );
    }
  }

  /**
   * Upright 3D torch post centered in the voxel space.
   * Post extends from y to y + 0.625, with width 0.125 (2/16ths of a block).
   * Emits top, bottom, and 4 sides with authentic flame and oak stick texture mapping.
   */
  private emitTorch(buf: MeshBuffer, wx: number, y: number, wz: number): void {
    const uv = TILE_UV[TILE.torch];
    const du = uv.u1 - uv.u0;
    const dv = uv.v1 - uv.v0;

    const x0 = wx + 0.4375;
    const x1 = wx + 0.5625;
    const z0 = wz + 0.4375;
    const z1 = wz + 0.5625;
    const y0 = y;
    const y1 = y + 0.625;
    const r = 1.0;
    const g = 0.95;
    const b = 0.8;

    // UVs accurately mapped to 2x10 pixel torch post (cols 7..9, rows 6..16)
    const sideUV = {
      u0: uv.u0 + (7 / 16) * du,
      u1: uv.u0 + (9 / 16) * du,
      v0: uv.v0,
      v1: uv.v0 + (10 / 16) * dv,
    };
    const topUV = {
      u0: uv.u0 + (7 / 16) * du,
      u1: uv.u0 + (9 / 16) * du,
      v0: uv.v0 + (8 / 16) * dv,
      v1: uv.v0 + (10 / 16) * dv,
    };
    const bottomUV = {
      u0: uv.u0 + (7 / 16) * du,
      u1: uv.u0 + (9 / 16) * du,
      v0: uv.v0,
      v1: uv.v0 + (2 / 16) * dv,
    };

    // +Y (top)
    emitQuad(buf, [x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0], 0, 1, 0, topUV, r, g, b);
    // +Z (front)
    emitQuad(buf, [x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1], 0, 0, 1, sideUV, r, g, b);
    // -Z (back)
    emitQuad(buf, [x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0], 0, 0, -1, sideUV, r, g, b);
    // +X (right)
    emitQuad(buf, [x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1], 1, 0, 0, sideUV, r, g, b);
    // -X (left)
    emitQuad(buf, [x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0], -1, 0, 0, sideUV, r, g, b);
    // -Y (bottom)
    emitQuad(buf, [x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1], 0, -1, 0, bottomUV, r, g, b);
  }

  /**
   * Criss-cross diagonal quads for Cobwebs matching Minecraft 1.8.8.
   */
  private emitWeb(buf: MeshBuffer, wx: number, y: number, wz: number): void {
    const uv = TILE_UV[TILE.cobweb];
    const x0 = wx;
    const x1 = wx + 1;
    const z0 = wz;
    const z1 = wz + 1;
    const y0 = y;
    const y1 = y + 1;
    const r = 1.0;
    const g = 1.0;
    const b = 1.0;

    // Diagonal 1: (x0, z0) to (x1, z1) - front and back
    emitQuad(buf, [x0, y0, z0], [x1, y0, z1], [x1, y1, z1], [x0, y1, z0], 0.707, 0, -0.707, uv, r, g, b);
    emitQuad(buf, [x1, y0, z1], [x0, y0, z0], [x0, y1, z0], [x1, y1, z1], -0.707, 0, 0.707, uv, r, g, b);

    // Diagonal 2: (x1, z0) to (x0, z1) - front and back
    emitQuad(buf, [x1, y0, z0], [x0, y0, z1], [x0, y1, z1], [x1, y1, z0], 0.707, 0, 0.707, uv, r, g, b);
    emitQuad(buf, [x0, y0, z1], [x1, y0, z0], [x1, y1, z0], [x0, y1, z1], -0.707, 0, -0.707, uv, r, g, b);
  }

  private applyToChunk(chunk: Chunk, solid: MeshBuffer, water: MeshBuffer): void {
    // Opaque pass.
    if (solid.isEmpty) {
      if (chunk.solidMesh) {
        chunk.solidMesh.geometry.dispose();
        chunk.solidMesh.removeFromParent();
        chunk.solidMesh = null;
      }
    } else {
      const geo = solid.toGeometry();
      if (chunk.solidMesh) {
        chunk.solidMesh.geometry.dispose();
        chunk.solidMesh.geometry = geo;
      } else {
        chunk.solidMesh = new THREE.Mesh(geo, this.solidMaterial);
        chunk.solidMesh.name = `chunk-${chunk.cx}-${chunk.cz}`;
        chunk.solidMesh.frustumCulled = true;
        chunk.solidMesh.renderOrder = 0;
        this.parent.add(chunk.solidMesh);
      }
    }

    // Transparent water pass.
    if (water.isEmpty) {
      if (chunk.waterMesh) {
        chunk.waterMesh.geometry.dispose();
        chunk.waterMesh.removeFromParent();
        chunk.waterMesh = null;
      }
    } else {
      const geo = water.toGeometry();
      if (chunk.waterMesh) {
        chunk.waterMesh.geometry.dispose();
        chunk.waterMesh.geometry = geo;
      } else {
        chunk.waterMesh = new THREE.Mesh(geo, this.waterMaterial);
        chunk.waterMesh.name = `water-${chunk.cx}-${chunk.cz}`;
        chunk.waterMesh.renderOrder = 1;
        this.parent.add(chunk.waterMesh);
      }
    }
  }
}

/**
 * Only fully opaque blocks occlude. Liquids and non-opaque blocks like glass
 * must let light into the corners or everything underwater goes black.
 */
function isOccluder(id: number): boolean {
  return id > 0 && BLOCKS[id].opaque;
}

/**
 * Read one of the three voxels diagonally adjacent to a face corner.
 * `du`/`dv` are -1 or +1 along axisU / axisV.
 */
function sampleCorner(
  world: World,
  bx: number,
  y: number,
  bz: number,
  face: FaceDef,
  du: number,
  dv: number,
  dir: V3,
): boolean {
  let sx = bx + dir[0];
  let sy = y + dir[1];
  let sz = bz + dir[2];

  if (du !== 0) {
    if (face.axisU === 0) sx += du;
    else if (face.axisU === 1) sy += du;
    else sz += du;
  }
  if (dv !== 0) {
    if (face.axisV === 0) sx += dv;
    else if (face.axisV === 1) sy += dv;
    else sz += dv;
  }

  return isOccluder(world.getBlock(sx, sy, sz));
}

/** Classic 0-3 occlusion level: two touching sides force full shadow. */
function vertexAO(side1: boolean, side2: boolean, corner: boolean): number {
  if (side1 && side2) return 0;
  return 3 - ((side1 ? 1 : 0) + (side2 ? 1 : 0) + (corner ? 1 : 0));
}

function emitQuad(
  buf: MeshBuffer,
  p0: [number, number, number],
  p1: [number, number, number],
  p2: [number, number, number],
  p3: [number, number, number],
  nx: number,
  ny: number,
  nz: number,
  uv: { u0: number; v0: number; u1: number; v1: number },
  r: number,
  g: number,
  b: number,
): void {
  const base = buf.positions.length / 3;
  buf.positions.push(...p0, ...p1, ...p2, ...p3);
  buf.normals.push(nx, ny, nz, nx, ny, nz, nx, ny, nz, nx, ny, nz);
  buf.colors.push(r, g, b, r, g, b, r, g, b, r, g, b);
  buf.uvs.push(uv.u0, uv.v0, uv.u1, uv.v0, uv.u1, uv.v1, uv.u0, uv.v1);
  buf.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

export { FACES, AO_LEVELS, WATER_TOP, MeshBuffer };
